import * as React from 'react';

import type { AnnouncementDto } from '@/api/announcements-admin-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  announcementStateLabel,
  effectiveAnnouncementState,
} from '@/lib/announcement-labels';
import {
  formatDateTime,
  formatOpaqueId,
} from '@/routes/admin/communication/announcements-format';

// =============================================================================
// announcements-registre-table.tsx — Table du REGISTRE des annonces (R15-COM-03, DBU §2.7.3 l.1745).
// =============================================================================
// Colonnes (DEC-35) : titre, campagne cible, statut, date d'envoi, auteur, nombre de destinataires. Tri
// client sur TOUTES les colonnes (aria-sort). PAS de colonne « date de création » (hors périmètre DEC-35 :
// la colonne n'existe pas en base). WCAG 2.1 AA : <table> + <caption> + <th scope>, l'état est un LIBELLÉ
// (Badge = renfort couleur uniquement), identités opaques tronquées. Le statut affiché est l'état EFFECTIF
// (lazy read-side : une fenêtre de modification écoulée s'affiche « Verrouillée » sans sweep serveur).

/** Colonnes triables du registre. */
export type AnnouncementSortColumn =
  | 'titre'
  | 'campaign'
  | 'state'
  | 'sentAt'
  | 'createdBy'
  | 'nombreDestinataires';

export type AnnouncementSortDirection = 'asc' | 'desc';

export interface AnnouncementsRegistreTableProps {
  readonly rows: readonly AnnouncementDto[];
  readonly sortColumn: AnnouncementSortColumn;
  readonly sortDirection: AnnouncementSortDirection;
  readonly onSort: (column: AnnouncementSortColumn) => void;
  /** Ouvre une annonce dans le composer (édition brouillon ou fenêtre de modification). */
  readonly onOpen: (announcementId: string) => void;
  /** Annule une annonce Planifiee (bouton visible sur ces seules lignes). */
  readonly onCancel: (announcement: AnnouncementDto) => void;
  /** Horloge d'affichage (état effectif) — injectable en test. Défaut : maintenant. */
  readonly now?: Date;
}

/** Libellé d'affichage de la campagne cible (intitulé si résolu, sinon identifiant). */
function campaignLabel(row: AnnouncementDto): string {
  return row.campaignIntitule ?? row.campaignId;
}

/** Compare deux valeurs pour le tri (chaînes / nombres / null → fin). */
function compare(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'fr');
}

function sortValue(
  row: AnnouncementDto,
  column: AnnouncementSortColumn,
  now: Date,
): string | number | null {
  switch (column) {
    case 'titre':
      return row.titre;
    case 'campaign':
      return campaignLabel(row);
    case 'state':
      return announcementStateLabel(
        effectiveAnnouncementState(row, now),
      ).label;
    case 'sentAt':
      return row.sentAt;
    case 'createdBy':
      return row.createdBy;
    case 'nombreDestinataires':
      return row.nombreDestinataires;
  }
}

export function AnnouncementsRegistreTable({
  rows,
  sortColumn,
  sortDirection,
  onSort,
  onOpen,
  onCancel,
  now = new Date(),
}: AnnouncementsRegistreTableProps): React.JSX.Element {
  const sorted = React.useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const result = compare(
        sortValue(a, sortColumn, now),
        sortValue(b, sortColumn, now),
      );
      return sortDirection === 'asc' ? result : -result;
    });
    return copy;
  }, [rows, sortColumn, sortDirection, now]);

  const ariaSort = (
    column: AnnouncementSortColumn,
  ): 'ascending' | 'descending' | 'none' =>
    sortColumn === column
      ? sortDirection === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none';

  const sortableHeader = (
    column: AnnouncementSortColumn,
    label: string,
  ): React.JSX.Element => (
    <th scope="col" aria-sort={ariaSort(column)} className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 rounded-sm font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {label}
        <span aria-hidden="true" className="text-xs">
          {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Registre des annonces : titre, campagne cible, statut, date d'envoi, auteur,
          nombre de destinataires et actions
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            {sortableHeader('titre', 'Titre')}
            {sortableHeader('campaign', 'Campagne cible')}
            {sortableHeader('state', 'Statut')}
            {sortableHeader('sentAt', "Date d'envoi")}
            {sortableHeader('createdBy', 'Auteur')}
            {sortableHeader('nombreDestinataires', 'Destinataires')}
            <th scope="col" className="px-4 py-3 font-medium">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const displayState = effectiveAnnouncementState(row, now);
            const stateInfo = announcementStateLabel(displayState);
            return (
              <tr
                key={row.announcementId}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-3 align-top font-medium">{row.titre}</td>
                <td className="px-4 py-3 align-top">{campaignLabel(row)}</td>
                <td className="px-4 py-3 align-top">
                  {/* Libellé porteur d'info ; le Badge n'est qu'un renfort couleur (ST-NFR-03). */}
                  <Badge variant={stateInfo.variant} withDot={stateInfo.variant === 'refused'}>
                    {stateInfo.label}
                  </Badge>
                </td>
                <td className="px-4 py-3 align-top tabular-nums">
                  {formatDateTime(row.sentAt)}
                </td>
                <td
                  className="px-4 py-3 align-top font-mono text-muted-foreground"
                  aria-label={
                    row.createdBy === null
                      ? 'Auteur non disponible'
                      : `Auteur (identifiant ${row.createdBy})`
                  }
                >
                  {formatOpaqueId(row.createdBy)}
                </td>
                <td className="px-4 py-3 align-top tabular-nums">
                  {/* DEC-35 : rempli au fan-out ; « — » tant que non envoyée (jamais une valeur inventée). */}
                  {row.nombreDestinataires === null ? '—' : row.nombreDestinataires}
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpen(row.announcementId)}
                    >
                      Ouvrir
                    </Button>
                    {/* Annulation possible UNIQUEMENT sur une annonce Planifiee (garde serveur re-affirmée). */}
                    {row.state === 'Planifiee' ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onCancel(row)}
                      >
                        Annuler
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
